/* VIBEHOME — Signature interactions. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
    : null;
  const scan = (root = document) => {
    root.querySelectorAll('.vh-reveal:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  };
  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', () => scan());
  document.addEventListener('shopify:section:load', (e) => scan(e.target));

  /* ---------- Business-day delivery estimate (US federal holidays) ---------- */
  const nth = (y, m, wd, n) => { const d = new Date(y, m, 1); return new Date(y, m, 1 + ((wd - d.getDay() + 7) % 7) + (n - 1) * 7); };
  const last = (y, m, wd) => { const d = new Date(y, m + 1, 0); return new Date(y, m, d.getDate() - ((d.getDay() - wd + 7) % 7)); };
  const hcache = {};
  const holidays = (y) => {
    if (hcache[y]) return hcache[y];
    const list = [[0, 1], [5, 19], [6, 4], [10, 11], [11, 25]].map(([m, d]) => new Date(y, m, d))
      .concat([nth(y, 0, 1, 3), nth(y, 1, 1, 3), last(y, 4, 1), nth(y, 8, 1, 1), nth(y, 9, 1, 2), nth(y, 10, 4, 4)]);
    return (hcache[y] = new Set(list.map((d) => d.toDateString())));
  };
  const isBiz = (d) => d.getDay() % 6 !== 0 && !holidays(d.getFullYear()).has(d.toDateString());
  const addBiz = (start, n) => { const d = new Date(start); let k = 0; while (k < n) { d.setDate(d.getDate() + 1); if (isBiz(d)) k++; } return d; };

  class VhDelivery extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10) || 4;
      const max = Math.max(parseInt(this.dataset.max, 10) || 7, min);
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const range = this.querySelector('[data-range]');
      if (range) range.textContent = `${fmt.format(addBiz(now, min))} – ${fmt.format(addBiz(now, max))}`;
      const cut = this.querySelector('[data-cutoff]');
      if (cut) {
        const tick = () => {
          const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
          const end = new Date(et); end.setHours(14, 0, 0, 0);
          const ms = end - et;
          if (!isBiz(et) || ms <= 0) { cut.closest('[data-cutoff-wrap]')?.setAttribute('hidden', ''); return; }
          const h = Math.floor(ms / 3.6e6); const m = Math.floor((ms % 3.6e6) / 6e4);
          cut.textContent = `${h ? h + 'h ' : ''}${m}m`;
          cut.closest('[data-cutoff-wrap]')?.removeAttribute('hidden');
        };
        tick(); this._t = setInterval(tick, 30000);
      }
      this.classList.add('is-ready');
    }
    disconnectedCallback() { clearInterval(this._t); }
  }
  if (!customElements.get('vh-delivery')) customElements.define('vh-delivery', VhDelivery);

  /* ---------- Scrollers with arrow buttons ---------- */
  class VhScroller extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      if (!track) return;
      this.querySelectorAll('[data-nav]').forEach((btn) => btn.addEventListener('click', () => {
        const dir = btn.dataset.nav === 'next' ? 1 : -1;
        track.scrollBy({ left: dir * track.clientWidth * 0.86, behavior: reduceMotion ? 'auto' : 'smooth' });
      }));
    }
  }
  if (!customElements.get('vh-scroller')) customElements.define('vh-scroller', VhScroller);

  /* ---------- Accordions: one open at a time inside a group ---------- */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.closest('[data-accordion-group]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  /* ---------- Count-up numbers ---------- */
  const countIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target; countIO.unobserve(el);
      const to = parseFloat(el.dataset.count); const dec = (el.dataset.count.split('.')[1] || '').length;
      if (reduceMotion || isNaN(to)) { el.textContent = el.dataset.count; return; }
      const t0 = performance.now(); const dur = 1200;
      const step = (t) => { const p = Math.min(1, (t - t0) / dur); const v = to * (1 - Math.pow(1 - p, 3)); el.textContent = v.toFixed(dec); if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    });
  }, { threshold: 0.4 }) : null;
  const scanCount = (root = document) => root.querySelectorAll('[data-count]').forEach((el) => countIO && countIO.observe(el));
  if (document.readyState !== 'loading') scanCount(); else document.addEventListener('DOMContentLoaded', () => scanCount());
})();

/* ---------- Cart drawer: one-tap add-ons ---------- */
(() => {
  let busy = false;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-vh-add]');
    if (!btn || busy) return;
    e.preventDefault();
    busy = true; btn.classList.add('is-busy'); btn.setAttribute('aria-busy', 'true');
    const drawer = document.querySelector('cart-drawer-component');
    const wrap = drawer && drawer.closest('[id^="shopify-section-"]');
    const sectionId = wrap ? wrap.id.replace('shopify-section-', '') : null;
    const body = { items: [{ id: Number(btn.dataset.vhAdd), quantity: 1 }] };
    if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }, body: JSON.stringify(body) })
      .then((r) => { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then((res) => {
        const html = sectionId && res.sections ? res.sections[sectionId] : null;
        if (html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const freshDlg = doc.querySelector('cart-drawer-component dialog');
          const liveDlg = document.querySelector('cart-drawer-component dialog');
          if (freshDlg && liveDlg) liveDlg.innerHTML = freshDlg.innerHTML;
          const freshIcon = doc.querySelector('cart-icon');
          const liveIcon = document.querySelector('cart-icon');
          if (freshIcon && liveIcon) liveIcon.innerHTML = freshIcon.innerHTML;
        }
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: null, sourceId: 'vh-upsell', data: { source: 'vh-upsell' } } }));
      })
      .catch(() => { window.location.href = '/cart'; })
      .finally(() => { busy = false; });
  });
})();

/* ---------- Product gallery: endless swipe — past the last photo comes the first, and back ---------- */
(() => {
  const SEL = 'media-gallery slideshow-component[infinite]';
  let start = null;
  const begin = (x, y, sc) => {
    start = { x, y, sc, idx: sc.current, n: sc.slides ? sc.slides.length : 0 };
  };
  const finish = (x, y) => {
    if (!start) return;
    const { sc, idx, n } = start;
    const dx = x - start.x;
    const dy = y - start.y;
    start = null;
    if (n < 2 || Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
    // wait for the theme's own snap/drag animation to settle, then loop around
    if (dx < 0 && idx >= n - 1) setTimeout(() => { if (sc.current >= n - 1 && !sc.disabled) sc.select(n); }, 420);
    else if (dx > 0 && idx <= 0) setTimeout(() => { if (sc.current <= 0 && !sc.disabled) sc.select(-1); }, 420);
  };
  const skip = (t) => t.closest('slideshow-controls, button, a, model-viewer, video');
  document.addEventListener('touchstart', (e) => {
    const sc = e.target.closest && e.target.closest(SEL);
    if (!sc || e.touches.length !== 1 || skip(e.target)) return;
    begin(e.touches[0].clientX, e.touches[0].clientY, sc);
  }, { passive: true });
  document.addEventListener('touchend', (e) => {
    const t = e.changedTouches[0];
    if (t) finish(t.clientX, t.clientY);
  }, { passive: true });
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const sc = e.target.closest && e.target.closest(SEL);
    if (!sc || skip(e.target)) return;
    begin(e.clientX, e.clientY, sc);
  });
  document.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') finish(e.clientX, e.clientY);
  });
})();

/* ---------- Sticky add-to-cart: show early on phones ----------
   The theme only reveals the bar after the buy buttons are scrolled past.
   On phones the buttons sit ~1.5 screens down, so most visitors never see
   a buy button. Show the bar as soon as they start scrolling and hand
   control back to the theme once the real buttons come into view. */
(() => {
  const sticky = document.querySelector('sticky-add-to-cart');
  const bar = sticky && sticky.querySelector('[ref="stickyBar"]');
  const block = document.querySelector('.buy-buttons-block');
  if (!bar || !block) return;
  const phone = window.matchMedia('(max-width: 749px)');
  let ours = false;
  let raf = 0;
  const update = () => {
    raf = 0;
    if (!phone.matches) return;
    const notReached = block.getBoundingClientRect().top > window.innerHeight;
    if (notReached && window.scrollY > 120) {
      if (bar.dataset.stuck !== 'true') bar.dataset.stuck = 'true';
      ours = true;
    } else if (ours && !notReached) {
      bar.dataset.stuck = 'false';
      ours = false;
    }
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  update();
})();
